import React, { useCallback, useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { combineEventFilters } from "@formant/core/common/combineFilters";
import { defined } from "@formant/core/common/defined";
import { Volume2, VolumeOff } from "@formant/ui-kit";

import { track } from "../../../analytics";
import { useRouter } from "../../../context/RouterContext";
import { useStores } from "../../../context/StoreContext";
import { authStore } from "../../../stores";
import { hashObservable } from "../../../utils/hashObservable";
import { ShareFlow } from "../../Channel/ShareFlow";
import { DeviceMenu } from "../../common/DeviceMenu";
import { DeviceMenuViewer } from "../../common/DeviceMenuViewer";
import { FilterDrawer } from "../../common/FilterDrawer";
import { Header } from "../../common/Header";
import { Hotkey } from "../../common/hotkey/Hotkey";
import { RestrictedComponent } from "../../common/RestrictedComponent";
import { DeviceStatus } from "../../DeviceStatus";
import { emptyEventListState } from "../../EventList/EventListState";
import { HomeTabs } from "../../HomeTabs";
import { OverviewViewName } from "../../Overview/OverviewV2/OverviewV2";
import { MuteSettingsModal } from "../../ViewSmartFleet/MuteSettingsModal";
import styles from "../ViewDeviceHeader.scss";
import { ConnectedHeaderNotificationButton } from "./HeaderNotificationButton";
import { IViewDeviceHeaderProps } from "./IViewDeviceHeaderProps";
import { ButtonGroup, HeaderButton, Spacer } from "./NewDeviceHeader.styles";

class NewViewDeviceHeaderState {
    @hashObservable()
    public filterVisible: boolean = false;
}

function closeOtherPopups() {
    // FIXME Attempt to close any other popups. Left over from initial header refactor.
    document.querySelectorAll("#close-button").forEach(it => {
        if (it instanceof HTMLElement) {
            it.click();
        }
    });
}

interface INewViewDeviceHeaderProps extends IViewDeviceHeaderProps {
    mode?: "schedules" | "events";
}

export const NewViewDeviceHeader = observer(function NewViewDeviceHeader(
    props: INewViewDeviceHeaderProps,
) {
    const {
        mode,
        device,
        viewId,
        filter,
        context,
        layoutType,
        onChangeViewId,
        onAnnotate,
        onChangeFilter,
    } = props;

    const { id: deviceId } = device;

    const {
        appStore,
        deviceConfigurationStore,
        deviceStore,
        observeStore,
        onlineDevicesStore,
        viewStore,
    } = useStores();

    const { navigate, routeTo } = useRouter();
    const [state] = useState(() => new NewViewDeviceHeaderState());
    const [muteModalOpen, setMuteModalOpen] = useState(false);
    const [muted, setMuted] = useState<{ start: string; end: string } | null>(
        null,
    );

    const eventFilter = useMemo(
        () => combineEventFilters(props.context, emptyEventListState.filter),
        [props.context, emptyEventListState.filter],
    );

    const id = defined(device.id);
    const online = onlineDevicesStore.isOnline(id) || false;
    const isCurrentlyMuted =
        muted &&
        new Date() >= new Date(muted.start) &&
        new Date() <= new Date(muted.end);

    let views = viewStore.listMatchingSingleDevice(device.tags) ?? [];
    views = views.filter(val => val.name !== OverviewViewName);
    const showMutingFeatures = true;

    // Initial fetch and polling setup
    useEffect(() => {
        const fetchDeviceMutedStatus = async () => {
            if (id) {
                const fetchedDevice = deviceStore.get(id);
                if (fetchedDevice && fetchedDevice.muted) {
                    setMuted({
                        start: fetchedDevice.muted.start,
                        end: fetchedDevice.muted.end,
                    });
                } else {
                    setMuted(null);
                }
            }
        };

        // Initial fetch
        void fetchDeviceMutedStatus();

        // Set up polling every minute
        const intervalId = setInterval(fetchDeviceMutedStatus, 60000);

        // Cleanup on unmount
        return () => clearInterval(intervalId);
    }, [id, deviceStore]);

    const handleMuteSubmit = useCallback(
        async (startDate: Date, durationHours?: number) => {
            if (durationHours === 0) {
                // Update local state first
                setMuted(null);
                // Then make the API call
                await deviceStore.update(id, { muted: null });
                // Don't close the modal
                return;
            }

            if (!durationHours) {
                return;
            }

            const endDate = new Date(
                startDate.getTime() + durationHours * 60 * 60 * 1000,
            );

            const mutedState = {
                start: startDate.toISOString(),
                end: endDate.toISOString(),
            };

            // Update local state first
            setMuted(mutedState);
            // Then make the API call
            await deviceStore.update(id, {
                muted: mutedState,
            });
            // Don't close the modal after confirming
        },
        [id, deviceStore],
    );

    const handleMuteToggle = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            if (isCurrentlyMuted) {
                // If device is muted, unmute it using handleMuteSubmit for consistency
                await handleMuteSubmit(new Date(), 0);
            } else {
                // If device is not muted, show mute settings modal
                setMuteModalOpen(true);
            }
        },
        [isCurrentlyMuted, handleMuteSubmit],
    );

    const onClose = useCallback(
        (callback?: () => void) => {
            closeOtherPopups();

            deviceStore.issuingCommand = false;
            observeStore.editingDevice = false;
            observeStore.editingComment = undefined;
            appStore.sharing = false;
            state.filterVisible = false;
            callback?.();
        },
        [state, deviceStore, observeStore, appStore],
    );

    const onConfigureDevice = useCallback(async () => {
        await navigate(
            routeTo.editDevice({
                id: deviceId,
            }),
        );
    }, [deviceId, navigate, routeTo]);

    const onEditDevice = useCallback(() => {
        observeStore.editingDevice = true;
    }, [observeStore]);

    const onExport = useCallback(() => {
        track("Export telemetry");
        observeStore.exportTelemetry(filter);
    }, [observeStore, filter]);

    const onIssueCommand = useCallback(() => {
        deviceStore.issuingCommand = true;
    }, [deviceStore]);

    const onRetrieveOnDemandData = useCallback(() => {
        const { focusedStreamName } = observeStore;
        const deviceConfiguration = deviceId
            ? deviceConfigurationStore.get(deviceId)
            : undefined;

        const isOnDemand = new Set(
            (
                (deviceConfiguration &&
                    deviceConfiguration.document.telemetry?.streams) ||
                []
            )
                .filter(_ => _.onDemand)
                .map(_ => _.name),
        );

        appStore.onDemandData(
            (focusedStreamName &&
                isOnDemand.has(focusedStreamName) &&
                focusedStreamName) ||
                "",
        );
    }, [deviceId, observeStore, appStore]);

    const onShare = useCallback(() => {
        track("Share started");
        appStore.sharing = true;
    }, [appStore]);

    const onToggleFilter = useCallback(() => {
        const previous = state.filterVisible;
        onClose();
        state.filterVisible = !previous;
    }, [state, onClose, navigate]);

    const onShowCalendar = useCallback(() => {
        void navigate(`/devices/${deviceId}/schedules`);
    }, [navigate]);

    const onShowEvents = useCallback(() => {
        void navigate(`/devices/${deviceId}/events`);
    }, [deviceId, navigate]);

    const closeAndExport = useCallback(
        () => onClose(onExport),
        [onClose, onExport],
    );
    const closeAndIssueCommand = useCallback(
        () => onClose(onIssueCommand),
        [onClose, onIssueCommand],
    );
    const closeAndRetrieveOnDemandData = useCallback(
        () => onClose(onRetrieveOnDemandData),
        [onClose, onRetrieveOnDemandData],
    );
    const closeAndShare = useCallback(
        () => onClose(onShare),
        [onClose, onShare],
    );

    const contextButton = (
        <>
            <RestrictedComponent
                resource="portForwarding"
                requiredAccess="operator"
            >
                <DeviceMenu
                    deviceId={deviceId}
                    mode="observe"
                    onAnnotate={onAnnotate}
                    filter={context}
                    hasTimeline={mode === undefined}
                    showingFilters={state.filterVisible}
                    onToggleFilters={!mode ? onToggleFilter : undefined}
                />
            </RestrictedComponent>

            {!authStore.hasAccess("portForwarding", "operator") && (
                <DeviceMenuViewer
                    mode="observe"
                    showingFilters={state.filterVisible}
                    onToggleFilters={!mode ? onToggleFilter : undefined}
                />
            )}

            <Hotkey
                label="Issue command"
                code="shift+i"
                onHotkey={closeAndIssueCommand}
            />
            <Hotkey
                label="Configure device"
                code="shift+,"
                onHotkey={onConfigureDevice}
            />
            <Hotkey
                label="Retrieve on-demand Data"
                code="shift+r"
                onHotkey={closeAndRetrieveOnDemandData}
            />
            <Hotkey label="Share" code="shift+s" onHotkey={closeAndShare} />
            {layoutType !== "teleop" && (
                <Hotkey
                    label="Export"
                    code="shift+e"
                    onHotkey={closeAndExport}
                />
            )}
        </>
    );

    return (
        <>
            <Header
                title={device.name}
                className={classnames(
                    styles["view-device-header"],
                    state.filterVisible && styles["filter-visible"],
                    observeStore.fullscreen && styles.fullscreen,
                )}
            >
                <DeviceStatus id={id} online={online} />
                <HomeTabs
                    eventFilter={eventFilter}
                    context={context}
                    value={viewId}
                    device={device}
                    filter={filter}
                    filterActive={state.filterVisible}
                    onChangeViewId={onChangeViewId}
                    onRetrieveOnDemandData={closeAndRetrieveOnDemandData}
                    onEditDevice={onEditDevice}
                    onToggleFilter={onToggleFilter}
                    layoutType={layoutType || "observe"}
                    views={views}
                />
                <Spacer />
                <ButtonGroup>
                    {showMutingFeatures && (
                        <div
                            onClick={handleMuteToggle}
                            className="cursor-pointer p-2"
                        >
                            {isCurrentlyMuted ? (
                                <VolumeOff
                                    size="28"
                                    className="hover:!text-[var(--color-status-active)]"
                                />
                            ) : (
                                <Volume2
                                    size="28"
                                    className="hover:!text-[var(--color-status-active)]"
                                />
                            )}
                        </div>
                    )}
                    <ConnectedHeaderNotificationButton
                        context={context}
                        eventFilter={eventFilter}
                        onClick={onShowEvents}
                        selected={mode === "events"}
                    />
                    <HeaderButton
                        icon="calendar"
                        onClick={onShowCalendar}
                        data-tooltip="Schedule"
                        selected={mode === "schedules"}
                    />
                </ButtonGroup>
                {contextButton}
            </Header>
            <FilterDrawer
                visible={state.filterVisible}
                filter={filter}
                context={context}
                hideDevices={layoutType === "dashboard"}
                onChangeFilter={onChangeFilter}
            />
            {showMutingFeatures && (
                <MuteSettingsModal
                    open={muteModalOpen}
                    onOpenChange={setMuteModalOpen}
                    onSubmit={handleMuteSubmit}
                    initialMuteData={muted}
                />
            )}
            <RestrictedComponent
                resource="share"
                requiredAccess="operator"
                key="share"
            >
                {appStore.sharing && <ShareFlow device={device} />}
            </RestrictedComponent>
        </>
    );
});
